import { create } from 'zustand'

import { get as apiGet } from '@/api/request'
import type { Transaction } from '@/types/api'

interface Balance {
  BTC: number
  BLD: number
}

interface AssetsStore {
  balance: Balance | null
  totalBalanceUSD: number
  btcPrice: number
  balanceLoading: boolean
  balanceError: string | null
  _balanceInitialized: boolean
  transactions: Transaction[]
  txTotal: number
  txPage: number
  txLoading: boolean
  txError: string | null
  _txInitialized: boolean
  fetchBalance: () => Promise<void>
  fetchTransactions: (limit?: number) => Promise<void>
  setTxPage: (page: number) => void
  reset: () => void
}

export const useAssetsStore = create<AssetsStore>((set, get) => ({
  balance: null,
  totalBalanceUSD: 0,
  btcPrice: 0,
  balanceLoading: false,
  balanceError: null,
  _balanceInitialized: false,
  transactions: [],
  txTotal: 0,
  txPage: 1,
  txLoading: false,
  txError: null,
  _txInitialized: false,

  async fetchBalance() {
    if (!get()._balanceInitialized) {
      set({ balanceLoading: true, balanceError: null })
    }
    try {
      const [balRes, priceRes] = await Promise.all([
        apiGet<{ balance: Balance; totalBalanceUSD: number }>('/users/balance'),
        apiGet<{ price: number }>('/price/btc'),
      ])
      const patch: Partial<AssetsStore> = {}
      if (balRes.data?.data) {
        patch.balance = balRes.data.data.balance
        patch.totalBalanceUSD = balRes.data.data.totalBalanceUSD
      }
      if (priceRes.data?.data) {
        patch.btcPrice = priceRes.data.data.price
      }
      set({ ...patch, balanceError: null })
    } catch (err) {
      if (!get()._balanceInitialized) {
        set({ balanceError: err instanceof Error ? err.message : 'Failed' })
      }
    } finally {
      set({ balanceLoading: false, _balanceInitialized: true })
    }
  },

  async fetchTransactions(limit = 10) {
    if (!get()._txInitialized) {
      set({ txLoading: true, txError: null })
    }
    try {
      const res = await apiGet<{ transactions: Transaction[]; pagination: { total: number } }>(
        '/users/transactions',
        { params: { page: get().txPage, limit } },
      )
      if (res.data?.data) {
        set({
          transactions: res.data.data.transactions,
          txTotal: res.data.data.pagination?.total || 0,
          txError: null,
        })
      }
    } catch (err) {
      if (!get()._txInitialized) {
        set({ txError: err instanceof Error ? err.message : 'Failed' })
      }
    } finally {
      set({ txLoading: false, _txInitialized: true })
    }
  },

  setTxPage(page) {
    set({ txPage: page, _txInitialized: false })
  },

  reset() {
    set({
      balance: null,
      totalBalanceUSD: 0,
      btcPrice: 0,
      balanceLoading: false,
      balanceError: null,
      _balanceInitialized: false,
      transactions: [],
      txTotal: 0,
      txPage: 1,
      txLoading: false,
      txError: null,
      _txInitialized: false,
    })
  },
}))
